import { ChangeDetectorRef, Component, Inject, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatCalendar, MatCalendarCellClassFunction, MatCalendarCellCssClasses, MatDatepicker } from '@angular/material/datepicker';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import moment from 'moment';

@Component({
    selector: 'app-calendar-dialog',
    templateUrl: './calendar-dialog.component.html',
    styleUrls: ['./calendar-dialog.component.scss']
})
export class CalendarDialogComponent {

    @ViewChild('calendar') calendar!: MatCalendar<Date>;
    @ViewChild('picker') datepicker!: MatDatepicker<Date>;

    dateRange: FormGroup;
    selectedTime: string = '';
    selectedDays: string = '';
    scheduleType: any = null;
    dates: moment.Moment[] | { from: string; to: string } = [];
    selectedDates: Date[] = [];

    today = moment().startOf('day');
    maxDate = moment().add(4, 'months').endOf('day');
    scheduler_detail_key: any;

    constructor(
        public dialogRef: MatDialogRef<CalendarDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: any,
        private fb: FormBuilder,
        private cdr: ChangeDetectorRef
    ) {
        this.dates = data.dates || [];
        this.selectedDays = data.scheduleDay || '';
        this.selectedTime = data.ScheduleTime || '';
        this.scheduler_detail_key = data.scheduler_detail_key || '';
        this.scheduleType = data.scheduleType || '';

        this.dateRange = this.fb.group({
            from_date: [null],
            to_date: [null]
        });
    }

    ngOnInit() {
        if (Array.isArray(this.dates)) {
            // ✅ Handle array of dates
            this.dates = this.dates.map(d => moment(d).startOf('day'));
        } else if (this.dates && typeof this.dates === 'object' && 'from' in this.dates && 'to' in this.dates) {
            // ✅ Handle { from, to } object
            const fromDate = moment(this.dates.from).startOf('day');
            const toDate = moment(this.dates.to).startOf('day');
            this.dates = this.getDatesInRange(fromDate.toDate(), toDate.toDate()).map(d => moment(d));
        }

        this.preselectDays();

        // ✅ Patch form if valid dates exist
        if (this.dates.length > 0) {
            this.dateRange.patchValue({
                from_date: this.dates[0].toDate(),
                to_date: this.dates[this.dates.length - 1].toDate()
            });
        }
        this.cdr.detectChanges();  // Ensure change detection runs
        setTimeout(() => {
            if (this.calendar) {
                this.calendar.updateTodaysDate();
                this.cdr.detectChanges();
            }
        }, 0);
    }

    ngAfterViewInit() {
        setTimeout(() => {
            if ( this.selectedDays == 'all days' && this.datepicker) {
                this.datepicker.open();
                this.cdr.detectChanges();
            }
        }, 100);
    }

    preselectDays() {
        const today = new Date();
        this.selectedDates = [];

        if (this.scheduleType == 'daily' && this.selectedDays === 'all days') {
            for (let i = 0; i < 120; i++) {
                let date = new Date();
                date.setDate(today.getDate() + i);
                this.selectedDates.push(date);
            }
        }
        else if (this.scheduleType == 'Weekly') {
            const daysMap: any = {
                sunday: 0, monday: 1, tuesday: 2, wednesday: 3, thursday: 4, friday: 5, saturday: 6,
                sun: 0, mon: 1, tue: 2, wed: 3, thu: 4, fri: 5, sat: 6
            };

            const dayIndex = daysMap[this.selectedDays];
            for (let i = 0; i < 120; i++) {
                let date = new Date();
                date.setDate(today.getDate() + i);
                if (date.getDay() === dayIndex) {
                    this.selectedDates.push(date);
                }
            }
        }
        else if (this.scheduleType == 'monthly') {
            const selectedDayOfMonth = parseInt(this.selectedDays); // Convert string to number

            if (!isNaN(selectedDayOfMonth) && selectedDayOfMonth > 0 && selectedDayOfMonth <= 31) {
                for (let i = 0; i < 4; i++) {
                    let month = new Date(today.getFullYear(), today.getMonth() + i, selectedDayOfMonth);
                    if (month.getMonth() === (today.getMonth() + i) % 12) {
                        this.selectedDates.push(month);
                    }
                }
            }
        }

        setTimeout(() => {
            if (this.calendar) {
                this.calendar.updateTodaysDate();
            }
        }, 0);
    }

    isSelected: MatCalendarCellClassFunction<Date> = (date: Date): MatCalendarCellCssClasses => {
        if (!this.dates || !Array.isArray(this.dates)) return '';

        return this.dates.some(d => moment(d).isSame(moment(date), 'day')) ? 'selected' : '';
    };

    isDisabled = (date: Date): boolean => {

    // Convert incoming date to IST (local) without shifting date
    const momentDate = moment(date).startOf("day");  // LOCAL date
    const today = moment().startOf("day");           // LOCAL today
    const maxDateLocal = moment(this.maxDate).startOf("day");

    // Disable before today or after max date
    if (momentDate.isBefore(today) || momentDate.isAfter(maxDateLocal)) {
        return true;
    }

    // --- TIME CHECK --- only convert selectedTime (UTC → IST) ---
    if (momentDate.isSame(today, "day") && this.selectedTime) {

        // Current IST time
        const nowIST = moment();

        // selectedTime stored as UTC → convert directly to IST
        const selectedIST = moment.utc(this.selectedTime, "hh:mm A").local();

        // Keep today's date, replace only hh:mm
        const selectedISTToday = moment().set({
            hour: selectedIST.hour(),
            minute: selectedIST.minute(),
            second: 0,
            millisecond: 0
        });

        if (nowIST.isAfter(selectedISTToday)) {
            return true;
        }
    }

    // --- WEEKLY / DAILY schedule ---
    if ((this.scheduleType === "Weekly" || this.scheduleType === "Daily") && this.selectedDays) {

        const daysMap: any = {
            sun: 0, mon: 1, tue: 2, wed: 3, thu: 4, fri: 5, sat: 6,
            sunday: 0, monday: 1, tuesday: 2, wednesday: 3,
            thursday: 4, friday: 5, saturday: 6
        };

        const todayDay = momentDate.day();  // Already IST

        // Range (mon-fri)
        if (this.selectedDays.includes("-")) {
            const [start, end] = this.selectedDays
                .split("-")
                .map(d => daysMap[d.trim().toLowerCase()]);

            if (start !== undefined && end !== undefined) {
                return !(todayDay >= start && todayDay <= end);
            }
        }

        // Multiple days (mon, wed, fri)
        if (this.selectedDays.includes(",")) {
            const allowed = this.selectedDays
                .split(",")
                .map(d => daysMap[d.trim().toLowerCase()])
                .filter(v => v !== undefined);

            return !allowed.includes(todayDay);
        }

        // Single day
        const single = daysMap[this.selectedDays.trim().toLowerCase()];
        return todayDay !== single;
    }

    // --- MONTHLY schedule ---
    if (this.scheduleType === "Monthly") {
        const selectedDay = parseInt(this.selectedDays);
        return momentDate.date() !== selectedDay;
    }

    return false;
};

    select(event: Date | null) {
        if (!event) return;

        const date = moment(event).startOf('day');
        if (this.isDisabled(date.toDate())) return;

        if (this.scheduler_detail_key) {
            this.dates = [date];
        } else {
            if (!Array.isArray(this.dates)) {
                this.dates = this.getDatesInRange(moment(this.dates.from).toDate(), moment(this.dates.to).toDate())
                    .map(d => moment(d));
            }

            const index = this.dates.findIndex(d => d.isSame(date, 'day'));
            if (index < 0) {
                this.dates.push(date);
            } else {
                this.dates.splice(index, 1);
            }
        }
        this.calendar.updateTodaysDate();
        this.cdr.detectChanges();
    }


    dateFilter = (date: Date | null): boolean => {
        if (!date) return false;
        const momentDate = moment(date).startOf('day');
        return !this.isDisabled(momentDate.toDate());
    };

    dateRangeFilter = (date: Date | null): boolean => {
    if (!date) return false;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const maxDate = new Date();
    maxDate.setDate(today.getDate() + 14); // next 2 weeks
    maxDate.setHours(0, 0, 0, 0);

    return date >= today && date <= maxDate;
    };


    applyDate(rangeSelected: boolean) {
        if (rangeSelected) {
            const fromDate = moment(this.dateRange.get('from_date')?.value).startOf('day');
            const toDate = moment(this.dateRange.get('to_date')?.value).startOf('day');

            if (fromDate.isValid() && !toDate.isValid()) {
                this.selectedDates = [fromDate.toDate()];
                this.dates = [fromDate];
                this.dialogRef.close({ type: 'array', dates: this.dates.map(d => d.format("MM-DD-YYYY")) });
            } else if (fromDate.isValid() && toDate.isValid()) {
                this.selectedDates = this.getDatesInRange(fromDate.toDate(), toDate.toDate());
                this.dates = this.selectedDates.map(date => moment(date).startOf('day')); // Ensuring no time shift
                this.dialogRef.close({ type: 'range', dates: { from: fromDate.format("MM-DD-YYYY"), to: toDate.format("MM-DD-YYYY") } });
            }
        } else {
            if (Array.isArray(this.dates)) {
                this.dialogRef.close({ type: 'array', dates: this.dates.map(d => d.format("MM-DD-YYYY")) });
            }
        }

        this.cdr.detectChanges();
    }

    cancelSelection() {
        this.dialogRef.close(this.dates);
        this.cdr.detectChanges();
    }

    getDatesInRange(startDate: Date, endDate: Date): Date[] {
        const dates: Date[] = [];
        let currentDate = moment(startDate).startOf('day');  // Ensure it's at the start of the day

        while (currentDate.isSameOrBefore(moment(endDate).startOf('day'))) {
            dates.push(currentDate.toDate());
            currentDate = currentDate.add(1, 'day');
        }
        return dates;
    }


    closeDialog() {
        this.dialogRef.close([]);
    }
}